import { CheckCircle2, Pencil, Plus, RefreshCw, Wallet, X } from 'lucide-react';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { apiRequest, ApiError } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { canAdmin } from '../../auth/adminAccess';
import { WorkspaceLayout } from '../../layouts/WorkspaceLayout';
import { EmptyState, Field, Modal, Spinner } from '../../shared/components';

type Coupon = {
  coupon_id: string;
  code_id: string;
  public_code: string;
  sequence?: number;
  code: string;
  batch_id: string;
  value_points: number;
  funding_transaction_id?: string;
  starts_at: string;
  expires_at: string;
  status: 'unused' | 'used';
  used_at: string;
  merchant_id: string;
  place_id: string;
  place_name: string;
};

type CouponForm = { public_code: string; quantity: number; value_points: number; starts_at: string; expires_at: string };
// create = new shared code · topup = more coupons (more money) on an existing code
type CouponDialog = { mode: 'create' } | { mode: 'topup'; code: string };

function localDate(offsetDays = 0) {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function dateAfter(value: string, days: number) {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function inputDate(value: string) { return value ? value.slice(0, 10) : ''; }
function displayDate(value: string) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' });
}

export function MerchantCouponsPage() {
  const { session } = useAuth();
  const canCreate = canAdmin(session, 'system');
  const issueRequest = useRef<{ signature: string; id: string } | null>(null);
  const [singleCodeReady, setSingleCodeReady] = useState(false);
  const [available, setAvailable] = useState<number | null>(null);
  const [items, setItems] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [editError, setEditError] = useState('');
  const [notice, setNotice] = useState('');
  const [filter, setFilter] = useState<'all' | 'unused' | 'used'>('all');
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [couponDialog, setCouponDialog] = useState<CouponDialog | null>(null);
  const [dialogError, setDialogError] = useState('');
  const [form, setForm] = useState<CouponForm>({ public_code: 'THX500', quantity: 100, value_points: 500, starts_at: localDate(), expires_at: localDate(30) });

  useEffect(() => {
    if (!session?.expert_id) return;
    try {
      const previous = JSON.parse(sessionStorage.getItem(`beaw.coupon.issue.${session.expert_id}`) || 'null');
      if (!previous?.id || !previous?.signature) return;
      const [owner, previousForm] = JSON.parse(previous.signature);
      if (owner === session.expert_id && previousForm?.quantity && previousForm?.value_points) {
        issueRequest.current = previous;
        setForm({ ...previousForm, public_code: previousForm.public_code || 'THX500' });
        setNotice('มีคำขอสร้าง Coupon ที่ยังไม่ได้รับผลยืนยัน กดสร้างอีกครั้งด้วยข้อมูลเดิมเพื่อตรวจสอบและดำเนินการต่อ');
      }
    } catch { /* Ignore invalid local recovery data. */ }
  }, [session?.expert_id]);

  const load = async () => {
    setLoading(true); setSingleCodeReady(false); setError('');
    try {
      const data = await apiRequest<{ items: Coupon[]; code_mode?: string }>('/admin/marketing/merchant-coupons', { query: { admin_id: session?.expert_id, status: 'all' } });
      setItems(data.items || []);
      setSingleCodeReady(data.code_mode === 'single_code');
      if (data.code_mode !== 'single_code') setError('Backend ยังไม่รองรับ Single Code กรุณาอัปเดตระบบก่อนสร้างคูปอง');
      if (canCreate) {
        const wallet = await apiRequest<{ remaining: number }>('/admin/economy/system-wallet/summary');
        setAvailable(wallet.remaining);
      }
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : 'โหลด Merchant Coupon ไม่สำเร็จ'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [session?.expert_id]);

  const summary = useMemo(() => ({
    all: items.length,
    unused: items.filter((item) => item.status === 'unused').length,
    used: items.filter((item) => item.status === 'used').length,
  }), [items]);
  const visibleItems = useMemo(
    () => filter === 'all' ? items : items.filter((item) => item.status === filter),
    [filter, items],
  );

  const codeGroups = useMemo(() => {
    const groups = new Map<string, { total: number; used: number; last: number; unusedValue: number; latestValue: number; latestSequence: number }>();
    for (const item of items) {
      if (!item.sequence) continue;
      const code = item.public_code;
      const group = groups.get(code) || { total: 0, used: 0, last: 0, unusedValue: 0, latestValue: 0, latestSequence: 0 };
      group.total++;
      if (item.status === 'used') { group.used++; group.last = Math.max(group.last, item.sequence); }
      else group.unusedValue += item.value_points;
      if (item.sequence > group.latestSequence) { group.latestSequence = item.sequence; group.latestValue = item.value_points; }
      groups.set(code, group);
    }
    return Array.from(groups.entries());
  }, [items]);

  const openCreate = () => {
    setDialogError('');
    // Keep a recovered, not-yet-confirmed request as is; otherwise start fresh.
    if (!issueRequest.current) setForm({ public_code: '', quantity: 100, value_points: 500, starts_at: localDate(), expires_at: localDate(30) });
    setCouponDialog({ mode: 'create' });
  };
  const openTopup = (code: string, latestValue: number) => {
    setDialogError('');
    setForm({ public_code: code, quantity: 10, value_points: latestValue || 500, starts_at: localDate(), expires_at: localDate(30) });
    setCouponDialog({ mode: 'topup', code });
  };

  const createCoupons = async (event: FormEvent) => {
    event.preventDefault(); if (!canCreate || submitting || !singleCodeReady) return;
    const signature = JSON.stringify([session?.expert_id, form]);
    const requestStorageKey = `beaw.coupon.issue.${session?.expert_id}`;
    if (issueRequest.current?.signature !== signature) {
      let previous: { signature: string; id: string } | null = null;
      try { previous = JSON.parse(sessionStorage.getItem(requestStorageKey) || 'null'); } catch { /* Start a fresh request. */ }
      issueRequest.current = previous?.signature === signature ? previous : { signature, id: crypto.randomUUID() };
    }
    sessionStorage.setItem(requestStorageKey, JSON.stringify(issueRequest.current));
    setSubmitting(true); setDialogError(''); setNotice('');
    try {
      // Top-ups reuse the same endpoint: an existing code continues its sequence.
      const data = await apiRequest<{ items: Coupon[] }>('/admin/marketing/merchant-coupons', { method: 'POST', body: { ...form, per_place_limit: 1, admin_id: session?.expert_id, request_id: issueRequest.current.id } });
      const amount = (form.quantity * form.value_points).toLocaleString('th-TH');
      setNotice(couponDialog?.mode === 'topup'
        ? `เพิ่มเงินเข้าโค้ด ${form.public_code} แล้ว · ${data.items.length.toLocaleString('th-TH')} สิทธิ์ รวม ${amount} บาท`
        : `สร้าง Merchant Coupon สำเร็จ ${data.items.length.toLocaleString('th-TH')} สิทธิ์ · โค้ด ${form.public_code}`);
      sessionStorage.removeItem(requestStorageKey);
      issueRequest.current = null;
      setForm((current) => ({ ...current, quantity: 1 }));
      setCouponDialog(null);
      await load();
    } catch (reason) { setDialogError(reason instanceof ApiError ? reason.message : 'สร้าง Merchant Coupon ไม่สำเร็จ'); }
    finally { setSubmitting(false); }
  };

  const saveCoupon = async (event: FormEvent) => {
    event.preventDefault(); if (!editing || submitting) return;
    setSubmitting(true); setEditError(''); setNotice('');
    try {
      await apiRequest(`/admin/marketing/merchant-coupons/${encodeURIComponent(editing.coupon_id)}`, { method: 'PATCH', body: {
        admin_id: session?.expert_id,
        value_points: editing.value_points,
        starts_at: inputDate(editing.starts_at),
        expires_at: inputDate(editing.expires_at),
      } });
      setEditing(null); setNotice(`บันทึกคูปอง ${editing.code_id || editing.coupon_id} แล้ว`); await load();
    } catch (reason) { setEditError(reason instanceof ApiError ? reason.message : 'แก้ไข Merchant Coupon ไม่สำเร็จ'); }
    finally { setSubmitting(false); }
  };

  return <WorkspaceLayout kind="admin" title="Merchant Coupon" description="ใช้โค้ดกลางร่วมกัน แยก Code ID แต่ละสิทธิ์เพื่อติดตามลำดับและร้านที่รับเงิน">
    {error && <p className="form-error">{error}</p>}{notice && <p className="success-notice"><CheckCircle2 />{notice}</p>}

    <section className="panel coupon-list-panel">
      <div className="panel-title"><div><h2>รายการ Code ID</h2><p>แสดงแต่ละสิทธิ์ตามลำดับ พร้อมวันใช้และร้านที่รับเงิน</p></div><div className="coupon-header-actions"><button className="button button--ghost" onClick={load}><RefreshCw />รีเฟรช</button>{canCreate && <button className="button button--primary" disabled={!singleCodeReady} onClick={openCreate}><Plus />สร้าง Merchant Coupon</button>}</div></div>
      {Boolean(codeGroups.length) && <div className="coupon-code-groups">{codeGroups.map(([code, group]) => <div key={code} className="coupon-code-group">
        <span><strong>{code}</strong><small>ใช้แล้ว {group.used} / {group.total} สิทธิ์ · คงเหลือ {group.total - group.used} ({group.unusedValue.toLocaleString('th-TH')} บาท) · ลำดับที่ใช้ล่าสุด {group.last || '—'}</small></span>
        {canCreate && <button className="button button--ghost" disabled={!singleCodeReady} onClick={() => openTopup(code, group.latestValue)}><Wallet />เพิ่มเงิน</button>}
      </div>)}</div>}
      <div className="coupon-filters" role="group" aria-label="กรองสถานะ Coupon">
        {([['all', 'ทั้งหมด'], ['unused', 'ยังไม่ได้ใช้'], ['used', 'ใช้แล้ว']] as const).map(([value, label]) => <button key={value} className={filter === value ? 'active' : ''} onClick={() => setFilter(value)}>{label}<b>{summary[value]}</b></button>)}
      </div>
      {loading ? <Spinner /> : !visibleItems.length ? <EmptyState title={items.length ? "ไม่มี Coupon ในสถานะนี้" : "ยังไม่มี Merchant Coupon"} description={items.length ? "เลือกสถานะอื่นเพื่อดูรายการ" : "กด “สร้าง Merchant Coupon” เพื่อสร้างรหัสแรก"} /> : <div className="table-scroll"><table className="coupon-table"><thead><tr><th>โค้ดกลาง</th><th>Code ID / ลำดับ</th><th>มูลค่า</th><th>ช่วงใช้งาน</th><th>สถานะ</th><th>ใช้เมื่อ / ร้าน</th><th aria-label="การทำงาน" /></tr></thead><tbody>{visibleItems.map((item) => <tr key={item.coupon_id}>
        <td>{item.sequence ? <code>{item.public_code}</code> : <span>คูปองรหัสเดิม</span>}</td><td><code>{item.code_id || item.coupon_id}</code>{item.sequence && <small>ลำดับ {item.sequence}</small>}</td><td><strong>{item.value_points.toLocaleString('th-TH')} บาท</strong></td>
        <td><span>{displayDate(item.starts_at)}</span><small>ถึง {displayDate(item.expires_at)}</small></td>
        <td><span className={`coupon-status coupon-status--${item.status}`}>{item.status === 'used' ? 'ใช้แล้ว' : 'ยังไม่ได้ใช้'}</span></td>
        <td>{item.status === 'used' ? <><span>{displayDate(item.used_at)}</span><small>{item.place_name || item.place_id || item.merchant_id || 'ไม่ระบุร้าน'}</small></> : <span>—</span>}</td>
        <td>{item.status === 'unused' && <button className="icon-button icon-button--table" aria-label={`แก้ไข ${item.code_id || item.coupon_id}`} onClick={() => { setEditError(''); setEditing(item); }}><Pencil /></button>}</td>
      </tr>)}</tbody></table></div>}
    </section>

    {couponDialog && canCreate && <Modal title={couponDialog.mode === 'topup' ? `เพิ่มเงินเข้าโค้ด ${couponDialog.code}` : 'สร้าง Merchant Coupon'} onClose={() => { if (!submitting) setCouponDialog(null); }}>
      <div className="coupon-dialog-body">
        <p className="coupon-dialog-lead">{couponDialog.mode === 'topup' ? 'เพิ่มสิทธิ์ใหม่ภายใต้โค้ดเดิม ระบบต่อลำดับ Code ID จากเดิม ร้านที่เคยใช้โค้ดนี้แล้วยังใช้ซ้ำไม่ได้' : 'ร้านกรอกโค้ดเดียวกัน เช่น THX500 ระบบใช้ Code ID ตามลำดับ แต่ละร้านรับได้ 1 สิทธิ์ต่อโค้ด'}</p>
      <form className="coupon-create-form" onSubmit={createCoupons}>
          <Field label="โค้ดกลาง (Single Code)"><input required pattern="[A-Z0-9]{6}" minLength={6} maxLength={6} value={form.public_code} placeholder="THX500" readOnly={couponDialog?.mode === 'topup'} onChange={event => setForm({ ...form, public_code: event.target.value.replace(/[^a-z0-9]/gi, '').toUpperCase() })} /></Field>
          <Field label="การใช้สิทธิ์ต่อร้าน"><span><input aria-label="ใช้ได้ร้านละ 1 ครั้ง (ล็อก)" type="checkbox" checked disabled readOnly /> ใช้ได้ร้านละ 1 ครั้ง (ล็อก)</span></Field>
          <Field label="จำนวนสิทธิ์ / Code ID"><input required min={1} max={500} type="number" value={form.quantity} onChange={(event) => setForm({ ...form, quantity: Number(event.target.value) })} /></Field>
          <Field label="ราคาต่อชิ้น (บาท)"><input required min={1} max={10000000} type="number" value={form.value_points} onChange={(event) => setForm({ ...form, value_points: Number(event.target.value) })} /></Field>
          <Field label="วันเริ่ม"><input required type="date" value={form.starts_at} onChange={(event) => setForm({ ...form, starts_at: event.target.value, expires_at: dateAfter(event.target.value, 30) })} /></Field>
          <Field label="วันหมดอายุ"><input required type="date" value={form.expires_at} onChange={(event) => setForm({ ...form, expires_at: event.target.value })} /></Field>
          <p>ยอดที่จะหักจาก System Wallet: <strong>{(form.quantity * form.value_points).toLocaleString('th-TH')} บาท</strong>{available !== null && <> · System Wallet ใช้ได้ {available.toLocaleString('th-TH')} บาท</>}</p>
          {dialogError && <p className="form-error">{dialogError}</p>}
          <footer className="coupon-dialog-actions"><button type="button" className="button button--ghost" onClick={() => setCouponDialog(null)}>ยกเลิก</button><button disabled={submitting || !singleCodeReady} className="button button--primary">{couponDialog?.mode === 'topup' ? <Wallet /> : <Plus />}{submitting ? 'กำลังบันทึก…' : couponDialog?.mode === 'topup' ? `เพิ่มเงิน ${(form.quantity * form.value_points).toLocaleString('th-TH')} บาท` : 'สร้าง Coupon'}</button></footer>
        </form>
      </div>
    </Modal>}
    {editing && <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setEditing(null)}><section className="modal coupon-edit-modal" role="dialog" aria-modal="true" aria-labelledby="coupon-edit-title"><header><div><p className="eyebrow">EDIT COUPON</p><h2 id="coupon-edit-title">{editing.code_id || editing.coupon_id}</h2></div><button className="icon-button" onClick={() => setEditing(null)} aria-label="ปิด"><X /></button></header><form onSubmit={saveCoupon}>
      <p>🔒 ใช้ได้ร้านละ 1 ครั้งต่อโค้ด — ไม่สามารถเปลี่ยนได้</p>
      <Field label="ราคาต่อชิ้น (บาท)"><input required min={1} max={10000000} type="number" disabled={Boolean(editing.funding_transaction_id)} value={editing.value_points} onChange={(event) => setEditing({ ...editing, value_points: Number(event.target.value) })} /></Field>
      {editing.funding_transaction_id && <p>มูลค่าถูกหักจาก System Wallet แล้ว จึงเปลี่ยนมูลค่าไม่ได้</p>}
      <Field label="วันเริ่ม"><input required type="date" value={inputDate(editing.starts_at)} onChange={(event) => setEditing({ ...editing, starts_at: event.target.value })} /></Field>
      <Field label="วันหมดอายุ"><input required type="date" value={inputDate(editing.expires_at)} onChange={(event) => setEditing({ ...editing, expires_at: event.target.value })} /></Field>
      {editError && <p className="form-error coupon-edit-error">{editError}</p>}
      <footer><button type="button" className="button button--ghost" onClick={() => setEditing(null)}>ยกเลิก</button><button disabled={submitting} className="button button--primary">บันทึกการแก้ไข</button></footer>
    </form></section></div>}
  </WorkspaceLayout>;
}
